// The Server-Sent-Events reader for `POST /v1/neo/chat/stream` (ADR 0117).
//
// This lived inside components/NeoChat.tsx until the Alarms page's mate bar
// needed the SAME transport: one wire format, one parser. It was moved here
// VERBATIM — the framing rules, the comment-frame skip and the silent
// JSON-parse bail are unchanged, so the chat panel behaves exactly as it did
// before the move. Anything that reads this stream reads it the same way.
//
// Pure enough to test under Node (cloud/web's vitest has no DOM): it takes a
// ReadableStream and calls back per decoded event.

/** One decoded SSE event payload. The server uses named events
 * (delta/reset/replace/mutations/alarm_proposal/alarm_changes/
 * alarm_change_requests/preferences/memories/recall/board/charts/cards/answer/agent/done/error); the
 * fields below are the union of their `data` shapes. */
export interface NeoStreamEvent {
  // A specialist Neo consults inside the turn, on an `agent` frame (TOM-543,
  // cloud ADR 0341 §7): `phase` 'start' or 'end', its `agent` id, the `label`
  // the busy row names ("your boards") and, on 'end', how it ended. Value-free,
  // and sent only to a request listing the `agents` capability. Decoded
  // defensively — see lib/neoAgentProgress.parseNeoAgentEvent.
  phase?: unknown;
  agent?: unknown;
  label?: unknown;
  status?: unknown;
  // The turn's id on the `turn` frame the stream opens with (ADR 0264): what
  // Stop cancels (TOM-671).
  turnRef?: string;
  text?: string;
  content?: string;
  conversationId?: string | null;
  // Persisted assistant message id on a `done` frame (ADR 0119) — wired to the
  // bubble so the owner can leave thumbs feedback on a streamed answer.
  messageId?: string;
  code?: string;
  message?: string;
  // Applied dashboard writes on a `mutations` frame (ADR 0128). Reported by
  // the server independent of the model's narration — rendered as a chip and
  // broadcast so the Instruments page refetches.
  mutations?: Array<{ tool: string; boatId: string; version: number | null; summary: string }>;
  // Drafted alarms awaiting the owner's click on an `alarm_proposal` frame
  // (ADR 0161). Reported independent of the narration and emitted on the aborted
  // turn too, so the cards appear even when the answer never finished. Decoded
  // defensively — see lib/neoAlarmProposal.parseAlarmProposals.
  proposals?: unknown;
  // Alarm changes (ADR 0210): changes Neo MADE on an `alarm_changes` frame, and
  // life-safety changes awaiting the owner's click on `alarm_change_requests`.
  // Decoded defensively — see lib/neoAlarmChange. `preferences` (ADR 0225) and
  // `memories` (ADR 0226) and `board` (ADR 0239) frames carry `changes` too —
  // see lib/neoPreferences, lib/neoMemories and lib/neoBoard.
  changes?: unknown;
  requests?: unknown;
  // The owner's past conversations the turn searched (a count only), opened or
  // forgot, on a `recall` frame (TOM-253). Decoded defensively — see
  // lib/neoRecall.parseRecallLookups.
  lookups?: unknown;
  // Change cards admin Neo drafted on a `change_drafts` frame (ADR 0231), on
  // the admin stream only. Decoded defensively — see admin/neoChanges.
  drafts?: unknown;
  // The charts a VALID answer shows, on a `charts` frame after its final text
  // and before `done` (ADR 0230). Decoded defensively — see lib/neoChart.
  charts?: unknown;
  // The answer charts on the same frame, for a client of `answers` (TOM-711,
  // cloud ADR 0403): the charts a v1 reader does not draw.
  answerCharts?: unknown;
  // The cards a VALID answer shows, on a `cards` frame after its charts
  // (ADR 0266). The web asks for maps, diagrams and pictures and draws only
  // those (TOM-468, TOM-643, TOM-646) — see lib/neoAnswerCards.parseNeoAnswerCards.
  cards?: unknown;
  // A VALID answer's record, on an `answer` frame after its cards and before
  // `done` (TOM-709, the answers contract §2), sent only to a request listing
  // the `answers` capability. Decoded defensively with parseNeoAnswer from
  // @neomaris/protocol: a record the chat cannot read is no record.
  answer?: unknown;
}

// Parse a Server-Sent-Events response body, invoking `onEvent(name, data)` per
// event. Tolerates the multi-line SSE framing and `:`-prefixed comments (the
// server sends `: open` to flush headers, and `: ping` every 15 s so a long
// turn, such as one drawing a picture, is not cut off as idle: TOM-645).
// Resolves when the stream ends.
export async function readNeoStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: NeoStreamEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary: number;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (data.length === 0) continue; // comment-only frame (": open", ": ping")
      let parsed: NeoStreamEvent;
      try {
        parsed = JSON.parse(data) as NeoStreamEvent;
      } catch {
        continue;
      }
      onEvent(event, parsed);
    }
  }
}
